import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Appointment } from '../../interfaces';
import useUser from '../global/useUser';
import { getAppointments } from '../../services/appointments';

function useGetAppointments({
  dataEndDate,
  dataStartDate,
  refetchInterval,
  includeCanceled,
  isReversed,
  limit,
  onlyIncomplete,
  useExactDates,
  customerId,
  employeeId,
  enabled = true,
}: {
  dataStartDate: Date | string;
  dataEndDate: Date | string;
  refetchInterval?: number;
  includeCanceled?: boolean;
  isReversed?: boolean;
  limit?: number;
  onlyIncomplete?: boolean;
  useExactDates?: boolean;
  customerId?: string;
  employeeId?: string;
  enabled?: boolean;
}) {
  const { user: loadedUser } = useUser();

  const queryKey = React.useMemo(
    () => [
      'appointments',
      {
        businessId: loadedUser?.business?.id,
        dataStartDate,
        dataEndDate,
        includeCanceled,
        isReversed,
        onlyIncomplete,
        useExactDates,
        customerId,
        employeeId,
      },
    ],
    [
      loadedUser?.business?.id,
      dataStartDate,
      dataEndDate,
      includeCanceled,
      isReversed,
      onlyIncomplete,
      useExactDates,
      customerId,
      employeeId,
    ],
  );
  const {
    data: appointments,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
  } = useQuery<Appointment[]>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: () =>
      getAppointments({
        businessId: loadedUser?.business?.id ?? '',
        startDate: dataStartDate,
        endDate: dataEndDate,
        includeCanceled,
        isReversed,
        limit,
        onlyIncomplete,
        useExactDates,
        customerId: customerId || undefined,
        employeeId: employeeId || undefined,
      }),
    enabled: !!loadedUser && enabled,
    refetchInterval,
  });

  return {
    queryKey,
    appointments,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
  };
}

export default useGetAppointments;
